/**
 * Domínio Financeiro — contratos, faturas, recebimentos e meta de receita.
 *
 * Convenções obrigatórias, iguais no adaptador demo e no PostgreSQL:
 *
 * - **Dinheiro é inteiro em centavos.** Nenhum valor monetário do domínio é
 *   ponto flutuante; a formatação em reais é responsabilidade da interface.
 * - **Data de calendário é `YYYY-MM-DD`.** Vencimento, início, encerramento e
 *   data de recebimento são datas civis, não instantes: elas não deslocam com
 *   fuso. O carimbo de criação continua sendo `datetime` UTC (AGENTS §7.6).
 * - **O contrato é do lead, não da empresa.** Um lead é a oportunidade em um
 *   funil (§7.1); a agregação por empresa é leitura, nunca vínculo.
 *
 * A regra central do domínio: o **primeiro recebimento da primeira mensalidade**
 * (ou do avulso) converte o lead em cliente e fecha a venda. Isso acontece em
 * uma única operação atômica junto com o recebimento — nunca em dois passos,
 * nunca inferido por nome de etapa.
 */

/** Contrato mensal recorrente ou trabalho avulso. */
export type ContractKind = "monthly" | "oneoff";

/** Origem da fatura: da série mensal, do avulso, ou um extra pontual. */
export type InvoiceKind = "monthly" | "oneoff" | "extra";

export interface FinanceContract {
  id: string;
  leadId: string;
  /** Participação no funil em que a venda foi fechada, quando conhecida. */
  membershipId?: string;
  kind: ContractKind;
  description: string;
  amountCents: number;
  /** Início da vigência. */
  startOn: string;
  /** Vencimento da primeira parcela; nunca antes de `startOn`. */
  firstDueOn: string;
  /** Fim previsto da vigência, quando o contrato tem prazo. */
  endOn?: string;
  /** Encerramento antecipado; cancela as faturas futuras em aberto. */
  cancelledOn?: string;
  /** Data do recebimento que fechou a venda. */
  closedOn?: string;
  /** Origem do lead no momento da assinatura, preservada para a análise. */
  sourceSnapshot?: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface FinanceInvoice {
  id: string;
  contractId: string;
  leadId: string;
  kind: InvoiceKind;
  /** Posição na série do contrato, começando em 1. */
  sequence: number;
  description: string;
  amountCents: number;
  dueOn: string;
  /** Cancelada pelo encerramento do contrato; sai das cobranças. */
  cancelledAt?: string;
  createdAt: string;
}

export interface FinanceReceipt {
  id: string;
  invoiceId: string;
  contractId: string;
  leadId: string;
  amountCents: number;
  receivedOn: string;
  /** Hora civil informada pelo usuário, `HH:MM`. */
  receivedAtTime: string;
  /** Verdadeiro no recebimento que converteu o lead em cliente. */
  firstSale: boolean;
  /** Origem do lead no momento do recebimento, para a receita por canal. */
  sourceSnapshot?: string;
  idempotencyKey: string;
  commandId: string;
  createdAt: string;
}

export interface RevenueGoal {
  id: string;
  periodStart: string;
  periodEnd: string;
  amountCents: number;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** Situação de uma fatura, derivada dos recebimentos e do vencimento. */
export type InvoiceStatus =
  | "open"
  | "partial"
  | "paid"
  | "overdue"
  | "partial_overdue"
  | "cancelled";

export const INVOICE_STATUS_LABEL: Record<InvoiceStatus, string> = {
  open: "Aberta",
  partial: "Parcial",
  paid: "Recebida",
  overdue: "Atrasada",
  partial_overdue: "Parcial e atrasada",
  cancelled: "Cancelada",
};

/** Horizonte padrão de geração de mensalidades, em dias. */
export const INVOICE_HORIZON_DAYS = 90;

/** Teto de parcelas geradas de uma vez; protege contra data de fim absurda. */
export const MAX_GENERATED_INVOICES = 1200;

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export function isCalendarDate(value: unknown): value is string {
  if (typeof value !== "string" || !DATE_PATTERN.test(value)) return false;
  const parsed = new Date(`${value}T12:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

export function isClockTime(value: unknown): value is string {
  return typeof value === "string" && TIME_PATTERN.test(value);
}

/**
 * Soma dias a uma data civil.
 *
 * O meio-dia UTC evita que horário de verão empurre a data para o dia anterior
 * ou seguinte — o mesmo cuidado que o protótipo já tinha.
 */
export function addDays(date: string, days: number): string {
  const moment = new Date(`${date}T12:00:00Z`);
  moment.setUTCDate(moment.getUTCDate() + days);
  return moment.toISOString().slice(0, 10);
}

/**
 * Avança `months` meses preservando o dia, com recuo para o último dia do mês.
 *
 * Um contrato que vence dia 31 cobra dia 30 em abril e 28 ou 29 em fevereiro,
 * sem pular para o mês seguinte.
 */
export function addMonths(date: string, months: number): string {
  const [year, month, day] = date.split("-").map(Number);
  const target = new Date(Date.UTC(year, month - 1 + months, 1, 12));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return target.toISOString().slice(0, 10);
}

/** Data a partir da qual o contrato não gera mais parcelas. */
export function contractCutoff(contract: FinanceContract): string {
  const limits = [contract.endOn, contract.cancelledOn].filter(Boolean) as string[];
  return limits.length > 0 ? limits.sort()[0] : "9999-12-31";
}

export function paidCents(receipts: readonly FinanceReceipt[], invoiceId: string): number {
  return receipts
    .filter((receipt) => receipt.invoiceId === invoiceId)
    .reduce((total, receipt) => total + receipt.amountCents, 0);
}

export function invoiceBalanceCents(
  invoice: FinanceInvoice,
  receipts: readonly FinanceReceipt[],
): number {
  return Math.max(0, invoice.amountCents - paidCents(receipts, invoice.id));
}

export function invoiceStatus(
  invoice: FinanceInvoice,
  receipts: readonly FinanceReceipt[],
  today: string,
): InvoiceStatus {
  const paid = paidCents(receipts, invoice.id);
  if (paid >= invoice.amountCents) return "paid";
  if (invoice.cancelledAt) return "cancelled";
  const overdue = invoice.dueOn < today;
  if (overdue) return paid > 0 ? "partial_overdue" : "overdue";
  return paid > 0 ? "partial" : "open";
}

/** Total já recebido de um lead; é o valor de cliente usado em Clientes. */
export function leadReceivedCents(
  receipts: readonly FinanceReceipt[],
  leadId: string,
): number {
  return receipts
    .filter((receipt) => receipt.leadId === leadId)
    .reduce((total, receipt) => total + receipt.amountCents, 0);
}
